import { useEffect, useMemo, useRef, useState } from "react";
import type { Item } from "../api/types";
import { useT } from "../i18n";
import { addDays, dayHeader, monthName, weekdayName, weekStart } from "../lib/dates";
import { agendaSort, dueDay, isLive } from "../lib/tasks";
import { useSyncState } from "../store";
import { updateTask } from "../sync/actions";
import { TaskRow } from "../tasks/TaskRow";
import { AddTask } from "../tasks/TaskTree";
import { IconChevronLeft, IconChevronRight } from "../ui/icons";
import { DatePicker } from "../ui/pickers";
import { usePrefs } from "../ui/prefs";
import { useAnchor } from "../ui/primitives";
import { SectionHeader } from "./sections";

const DAYS_SHOWN = 28;

export function UpcomingView() {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const [selected, setSelected] = useState(prefs.today);
  const resched = useAnchor();
  const monthA = useAnchor();
  const weekFrom = weekStart(selected, prefs.startDay);
  const listStart = weekFrom < prefs.today ? prefs.today : weekFrom;
  const scrollTo = useRef<string | null>(null);

  const byDay = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const it of Object.values(state.items)) {
      if (!isLive(state, it)) continue;
      const d = dueDay(it, prefs.tz);
      if (!d) continue;
      const key = d < prefs.today ? "overdue" : d;
      const list = map.get(key) ?? [];
      list.push(it);
      map.set(key, list);
    }
    for (const [k, v] of map) map.set(k, agendaSort(v, prefs.tz));
    return map;
  }, [state, prefs.tz, prefs.today]);

  useEffect(() => {
    const day = scrollTo.current;
    if (!day) return;
    scrollTo.current = null;
    document.querySelector(`[data-day-group="${day}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  });

  const select = (day: string) => {
    setSelected(day);
    scrollTo.current = day < prefs.today ? prefs.today : day;
  };

  const overdue = byDay.get("overdue") ?? [];
  const week = Array.from({ length: 7 }, (_, i) => addDays(weekFrom, i));
  const days = Array.from({ length: DAYS_SHOWN }, (_, i) => addDays(listStart, i));
  const [y, m] = [Number(selected.slice(0, 4)), Number(selected.slice(5, 7))];
  return (
    <div className="view" data-testid="upcoming-view">
      <div className="week-strip">
        <div className="view-header">
          <h1 className="view-title">{t("upcoming")}</h1>
        </div>
        <div className="week-nav">
          <button type="button" className="btn btn-ghost btn-sm" onClick={monthA.open} data-testid="upcoming-month">
            <strong>{prefs.lang === "zh" ? `${y}年${m}月` : `${monthName(m, "en", true)} ${y}`}</strong>
          </button>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              className="icon-btn"
              aria-label={t("previous")}
              data-testid="upcoming-prev"
              disabled={weekFrom <= weekStart(prefs.today, prefs.startDay)}
              onClick={() => select(addDays(weekFrom, -7))}
            >
              <IconChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="btn btn-chip"
              onClick={() => select(prefs.today)}
              data-testid="upcoming-today"
            >
              {t("goToday")}
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t("next")}
              data-testid="upcoming-next"
              onClick={() => select(addDays(weekFrom, 7))}
            >
              <IconChevronRight size={18} />
            </button>
          </div>
        </div>
        <div className="week-days">
          {week.map((d) => (
            <button
              key={d}
              type="button"
              className={`week-day${d < prefs.today ? " past" : ""}${d === prefs.today ? " today" : ""}${d === selected ? " selected" : ""}`}
              onClick={() => select(d)}
              disabled={d < prefs.today}
              data-testid="week-day"
              data-day={d}
            >
              <span>{weekdayName(d, prefs.lang, true)}</span>
              <span className="num">{Number(d.slice(8))}</span>
              <span className={`dot${byDay.get(d)?.length ? "" : " empty"}`} />
            </button>
          ))}
        </div>
      </div>
      {overdue.length > 0 && listStart === prefs.today && (
        <div className="section" data-testid="overdue-group">
          <SectionHeader
            title={t("overdue")}
            count={overdue.length}
            extra={
              <button type="button" className="reschedule" onClick={resched.open}>
                {t("reschedule")}
              </button>
            }
          />
          {overdue.map((it) => (
            <TaskRow key={it.id} item={it} showProject />
          ))}
        </div>
      )}
      {days.map((d) => {
        const list = byDay.get(d) ?? [];
        return (
          <div className="section" key={d} data-day-group={d} data-testid="day-group">
            <SectionHeader title={dayHeader(d, prefs.today, prefs.lang)} count={list.length} />
            {list.map((it) => (
              <TaskRow key={it.id} item={it} showProject hideDate={!it.due || it.due.date.length === 10} />
            ))}
            <AddTask defaults={{ due: { date: d } }} />
          </div>
        );
      })}
      {monthA.anchor && (
        <DatePicker
          anchor={monthA.anchor}
          onClose={monthA.close}
          value={{ date: selected, string: "", lang: "en", is_recurring: false, timezone: null }}
          onChange={(due) => {
            if (due && "date" in due) select(due.date < prefs.today ? prefs.today : due.date);
          }}
        />
      )}
      {resched.anchor && (
        <DatePicker
          anchor={resched.anchor}
          onClose={resched.close}
          value={null}
          allowRecurring={false}
          onChange={(due) => {
            for (const it of overdue) void updateTask(it.id, { due });
          }}
        />
      )}
    </div>
  );
}
